import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

type Status = "idle" | "signing-in" | "creating-new"

type State<T> = {
  credential: T | undefined
  status: Status
}

type Actions<T, P, C, O> = {
  setCredential: (passkey: T) => void
  signOut: () => void
  signIn: () => Promise<T>
  createNew: (passkeyName: string) => Promise<T>
  signMessage: (challenge: C, options?: O) => Promise<P>
}

type Store<T, P, C, O> = State<T> & Actions<T, P, C, O>

type PasskeyService<T, P, C, O> = {
  signIn: () => Promise<T>
  createNew: (passkeyName: string) => Promise<T>
  signMessage: (challenge: C, credential: T, options?: O) => Promise<P>
}

export const createWebAuthnStore = <T, P, C, O = void>(
  service: PasskeyService<T, P, C, O>
) => {
  return create<Store<T, P, C, O>>()(
    persist(
      (set, get) => ({
        credential: undefined,
        status: "idle" as Status,

        setCredential: (passkey) => {
          set({ credential: passkey })
        },

        signOut: () => set({ credential: undefined }),

        signIn: async () => {
          if (get().credential != null) {
            throw new Error("Already authenticated")
          }

          if (get().status !== "idle") {
            throw new Error("Authentication already in progress")
          }

          set({ status: "signing-in" })

          try {
            const credential = await service.signIn()
            set({ credential })
            return credential
          } finally {
            set({ status: "idle" })
          }
        },

        createNew: async (passkeyName) => {
          if (get().credential != null) {
            throw new Error("Already authenticated")
          }

          if (get().status !== "idle") {
            throw new Error("Authentication already in progress")
          }

          set({ status: "creating-new" })

          try {
            const credential = await service.createNew(passkeyName)
            set({ credential })
            return credential
          } finally {
            set({ status: "idle" })
          }
        },

        signMessage: async (challenge, options) => {
          const credential = get().credential
          if (credential == null) {
            throw new Error("Unauthenticated")
          }
          return service.signMessage(challenge, credential, options)
        },
      }),
      {
        name: "app_wallets_passkey",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ credential: state.credential }),
      }
    )
  )
}
